import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "helpers/api";
import endpoints from "helpers/endpoints";
import Machine from "types/Machine.type";
import * as constants from "helpers/constants";
import MachineSummary from "types/MachineSummary.type";
import ServiceRecord from "types/ServiceRecord.type";
import { useParams } from "react-router-dom";
import MachineLog from "types/MachineLog.type";
import useApi from "hooks/useApi";
import PackageBuildStatus from "types/PackageBuildStatus.enum";
import Permission from "types/Permission.enum";
import useHasPermissions from "hooks/useHasPermissions";

const addMachine = (data) => {
  return api({
    url: endpoints.Machines,
    method: "post",
    data
  });
};

const useAddMachine = (onMachineAdded) => {
  const result = useMutation({
    mutationFn: addMachine,
    onSettled: onMachineAdded,
    meta: {
      errorMessage: "E.MACHINE-006"
    }
  });

  return { ...result, addNewMachine: result.mutate };
};

const useUpdateMachine = (onMachineUpdated) => {
  return useMutation({
    mutationFn: (data: any) => {
      return api({
        url: `api/Machines`,
        params: {
          id: data.machineId
        },
        method: "put",
        data: {
          machineUseId: data.machineUseId,
          name: data.machineName,
          serialNumber: data.serial,
          organisationSiteId: data.clinic,
          serviceDue: data.serviceDue
        },
        headers: { "Content-Type": "application/json" }
      });
    },
    onSettled: onMachineUpdated,
    meta: {
      errorMessage: "E.MACHINE-009"
    }
  });
};

const fetchClinics = () => {
  return api({
    url: endpoints.Clinics,
    method: "get"
  });
};

const useClinics = () => {
  return useQuery({
    queryKey: ["clinic-list"],
    queryFn: fetchClinics,
    select: (data) => data.data.clinics,
    meta: {
      errorMessage: "E.MACHINE-007"
    }
  });
};

const fetchMachines = async ({ queryKey }) => {
  const fetchOptions = queryKey[2];
  return api({
    method: "get",
    url: endpoints.Machines,
    params: { ...fetchOptions }
  });
};

function useMachines(fetchOptions) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["machine", "list", fetchOptions],
    queryFn: fetchMachines,
    select: (data): { machineDetails: Machine[]; totalRecords: number } => {
      if (data.data.machineDetails) {
        return data.data.machineDetails;
      } else {
        return { machineDetails: [], totalRecords: 1 };
      }
    },
    initialData: () => {
      return queryClient.getQueryData(["machine", "list"], {
        exact: false,
        type: "active"
      });
    },
    meta: {
      errorMessage: "E.MACHINE-001"
    },
    keepPreviousData: true
  });
}

function useMachineSearch(fetchOptions) {
  return useQuery({
    queryKey: ["machine", "search", fetchOptions],
    queryFn: fetchMachines,
    select: (data): { machineDetails: Machine[]; totalRecords: number } => {
      if (data.data.machineDetails) {
        return data.data.machineDetails;
      } else {
        return { machineDetails: [], totalRecords: 1 };
      }
    },
    meta: {
      errorMessage: "E.MACHINE-001"
    },
    enabled: !!fetchOptions.searchText
  });
}

const fetchMachineSummary = async ({ queryKey }) => {
  const machineId = queryKey[2];

  return api({
    url: endpoints.MachineSummary(machineId)
  });
};

const useMachineSummary = (machineId) => {
  return useQuery({
    queryKey: ["machine", "detail", machineId],
    queryFn: fetchMachineSummary,
    select: (data): MachineSummary => {
      return data.data;
    },
    enabled: !!machineId,
    meta: {
      errorMessage: "E.MACHINE-010"
    }
  });
};

const fetchLocations = () => {
  return api({
    url: endpoints.Locations,
    method: "get"
  });
};

const useLocations = () => {
  return useQuery({
    queryKey: ["location-list"],
    queryFn: fetchLocations,
    select: (data) =>
      data.data.locations.map((location) => ({ id: location, location })),
    meta: {
      errorMessage: "E.MACHINE-025"
    }
  });
};
const fetchMachineSerial = async () => {
  return api({
    url: endpoints.MachineSerial
  });
};

const useMachineSerialNumber = () => {
  return useQuery({
    queryKey: ["machine-serial-number"],
    queryFn: fetchMachineSerial,
    select: (data) => data.data.serialNumbers,
    placeholderData: () => ({ data: { serialNumbers: [] } }),
    staleTime: constants.MACHINE_API_STALE_TIME, // 1 hour
    meta: {
      errorMessage: "E.MACHINE-008"
    }
  });
};

const fetchMachineUses = async () => {
  return api({
    url: endpoints.MachineUses
  });
};

const useMachineUses = () => {
  return useQuery({
    queryKey: ["machine-uses"],
    queryFn: fetchMachineUses,
    select: (data) => data.data.machineUses,
    placeholderData: () => ({ data: { machineUses: [] } }),
    staleTime: constants.MACHINE_API_STALE_TIME, // 1 hour
    meta: {
      errorMessage: "E.MACHINE-002"
    }
  });
};

const fetchMachineHardwareVersion = async () => {
  return api({
    url: endpoints.MachineHardwareVersion
  });
};

const useMachineHardwareVersion = () => {
  return useQuery({
    queryKey: ["machine-hardware-version"],
    queryFn: fetchMachineHardwareVersion,
    select: (data) => data.data.hardwareVersion,
    placeholderData: () => ({ data: { hardwareVersion: [] } }),
    staleTime: constants.MACHINE_API_STALE_TIME, // 1 hour
    meta: {
      errorMessage: "E.MACHINE-004"
    }
  });
};

const fetchMachineSoftwareVersion = async () => {
  return api({
    url: endpoints.MachineSoftwareVersion
  });
};

const useMachineSoftwareVersion = () => {
  return useQuery({
    queryKey: ["machine-software-version"],
    queryFn: fetchMachineSoftwareVersion,
    select: (data) => data.data.softwareVersion,
    placeholderData: () => ({ data: { softwareVersion: [] } }),
    staleTime: constants.MACHINE_API_STALE_TIME, // 1 hour
    meta: {
      errorMessage: "E.MACHINE-005"
    }
  });
};

const fetchServiceRecords = async ({ queryKey }) => {
  const machineId = queryKey[1];
  const fetchOptions = queryKey[2];
  return api({
    method: "get",
    url: endpoints.ServiceRecords(machineId),
    params: { ...fetchOptions },
    paramsSerializer: {
      indexes: null // by default: false
    }
  });
};

function useServiceRecords(fetchOptions) {
  const { machineId } = useParams();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["service-records", machineId, fetchOptions],
    queryFn: fetchServiceRecords,
    select: (
      data
    ): { serviceRecordDetails: ServiceRecord[]; totalRecords: number } => {
      if (data.data) {
        return data.data;
      } else {
        return { serviceRecordDetails: [], totalRecords: 0 };
      }
    },
    initialData: () => {
      return queryClient.getQueryData(["service-records"], {
        exact: false,
        type: "active"
      });
    },
    meta: {
      errorMessage: "E.MACHINE-011"
    },
    keepPreviousData: true
  });
}

const fetchMachineLogs = async ({ queryKey }) => {
  const serialNumber = queryKey[1];
  const fetchOptions = queryKey[2];
  return api({
    method: "get",
    url: endpoints.MachineLogs(serialNumber),
    params: fetchOptions
  });
};

function useMachineLogs(serialNumber, fetchOptions) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ["machine-logs", serialNumber, fetchOptions],
    queryFn: fetchMachineLogs,
    select: (data): { machineLogsData: MachineLog[] } => {
      if (data.data) {
        return data.data;
      } else {
        return { machineLogsData: [] };
      }
    },
    initialData: () => {
      return queryClient.getQueryData(["machine-logs"], {
        exact: false,
        type: "active"
      });
    },
    meta: {
      errorMessage: "E.MACHINE-013"
    },
    keepPreviousData: true
  });
}
const uploadLog = ({ serialNumber, file, onUploadProgress }) => {
  const data = new FormData();
  data.append("file", file);

  return api({
    url: endpoints.LogUpload(serialNumber),
    method: "post",
    data,
    onUploadProgress
  });
};

function useUploadLog(onSuccessfulUpload) {
  return useMutation({
    mutationFn: uploadLog,
    onSettled: onSuccessfulUpload,
    meta: { errorMessage: "E.MACHINE-023" }
  });
}

const fetchReasonForServiceType = () => {
  return api({
    url: endpoints.ReasonForServiceTypes,
    method: "get"
  });
};

const useReasonForServiceType = () => {
  return useQuery({
    queryKey: ["reason-for-service-type"],
    queryFn: fetchReasonForServiceType,
    select: (data): { id: number; name?: string }[] =>
      data.data.reasonForServices,
    staleTime: constants.MACHINE_API_STALE_TIME,
    meta: {
      errorMessage: "E.MACHINE-012"
    }
  });
};

const addServiceRecord = async (machineId) => {
  return api({
    method: "post",
    url: endpoints.ServiceRecords(machineId)
  });
};

function useAddServiceRecord(onServiceRecordFetch) {
  return useMutation({
    mutationFn: addServiceRecord,
    onSettled: onServiceRecordFetch,
    meta: {
      errorMessage: "E.MACHINE-011"
    }
  });
}

const useCompleteServiceRecord = (onCompleteServiceRecord, serviceRecordId) => {
  return useMutation({
    mutationFn: (data: any) => {
      return api({
        url: endpoints.ServiceRecord(serviceRecordId),
        method: "put",
        data,
        headers: { "Content-Type": "application/json" }
      });
    },
    onSettled: onCompleteServiceRecord,
    meta: {
      errorMessage: "E.MACHINE-018"
    }
  });
};

const fetchMachineLogTypes = async ({ queryKey }) => {
  const serialNumber = queryKey[1];
  return api({
    method: "get",
    url: endpoints.MachineLogTypes,
    params: { serialNumber }
  });
};

const useMachineLogTypes = (serialNumber) => {
  return useQuery({
    queryKey: [" machine-log-types", serialNumber],
    queryFn: fetchMachineLogTypes,
    select: (data): [] => data.data.logTypes,
    enabled: !!serialNumber,
    meta: {
      errorMessage: "E.MACHINE-022"
    }
  });
};

const useMachineLogFile = (serialNumber) => {
  return useApi({
    queryFn: ({ filePath, isDownload }) => {
      return api({
        method: "get",
        url: endpoints.DownloadLogFile(serialNumber),
        params: { fileName: filePath, isDownload }
      });
    },
    meta: { errorMessage: "E.MACHINE-020" }
  });
};

const fetchAllowedSoftwareUpgrade = async ({ queryKey }) => {
  const machineId = queryKey[1];

  return api({
    url: endpoints.AllowedSoftwareUpgrade,
    params: { machineId },
    method: "get"
  });
};

const useAllowedSoftwareUpgrade = (machineId) => {
  return useQuery({
    queryKey: ["software-upgrade", machineId],
    queryFn: fetchAllowedSoftwareUpgrade,
    enabled: !!machineId,
    select: (data) => data.data.softwareUpgradeForm,
    meta: {
      errorMessage: "E.MACHINE-021"
    }
  });
};

const softwareUpgrade = (data) => {
  return api({
    url: endpoints.SoftwareUpgradeRequest,
    method: "post",
    data
  });
};

const useSoftwareUpgradeRequest = (onSoftwareUpgrade) => {
  return useMutation({
    mutationFn: softwareUpgrade,
    onSettled: onSoftwareUpgrade,
    meta: {
      errorMessage: "E.MACHINE-017"
    }
  });
};

const fetchLatestSoftware = async ({ queryKey }) => {
  const machineId = queryKey[1];
  return api({
    url: endpoints.LatestSoftware(machineId),
    params: { machineId },
    method: "get"
  });
};

const useLatestSoftware = (machineId) => {
  return useQuery({
    queryKey: ["latest-software", machineId],
    queryFn: fetchLatestSoftware,
    select: (data) => data.data.latestSoftwareDetails ?? [],
    enabled: !!machineId,
    meta: {
      errorMessage: "E.MACHINE-015"
    },
    refetchInterval: (softwares = []) => {
      const isDownloadPending = softwares.some(
        (s) =>
          s.securePackageBuildStatusName === PackageBuildStatus.Requested ||
          s.securePackageBuildStatusName === PackageBuildStatus.Processing
      );

      return isDownloadPending
        ? constants.REFETCH_INTERVAL_LATEST_SOFTWARE
        : false;
    }
  });
};

const useMachineSoftware = (serialNumber) => {
  return useApi({
    queryFn: ({ filePath }) => {
      return api({
        method: "get",
        url: endpoints.DownloadSoftware(serialNumber),
        params: { fileName: filePath }
      });
    },
    meta: { errorMessage: "E.MACHINE-020" }
  });
};

const fetchLocationType = () => {
  return api({
    url: endpoints.LocationType,
    method: "get"
  });
};

const useLocationType = () => {
  const hasLocationTypePermission = useHasPermissions([
    Permission.ADD_UPDATE_MACHINE_SERVICE
  ]);

  return useQuery({
    queryKey: ["location-type"],
    queryFn: fetchLocationType,
    select: (data): { id: number; name?: string }[] => data.data.locationTypes,
    meta: {
      errorMessage: "MACHINE-024"
    },
    enabled: hasLocationTypePermission
  });
};

export {
  useMachineUses,
  useMachineSerialNumber,
  useMachineHardwareVersion,
  useMachineSoftwareVersion,
  useMachines,
  useAddMachine,
  useMachineSummary,
  useClinics,
  useUpdateMachine,
  useServiceRecords,
  useMachineLogs,
  useMachineLogTypes,
  useUploadLog,
  useReasonForServiceType,
  useAddServiceRecord,
  useCompleteServiceRecord,
  useAllowedSoftwareUpgrade,
  useSoftwareUpgradeRequest,
  useMachineSoftware,
  useLatestSoftware,
  useMachineLogFile,
  useLocationType,
  useMachineSearch,
  useLocations
};
